import {QueryClient, type QueryClientConfig} from '@tanstack/react-query';

/**
 * One client per page, created through this factory.
 *
 * A factory rather than `new QueryClient()` at each call site so the cache policy lives in one place, and
 * because unified-cloud recorded two specific mistakes worth not repeating: constructing a client inside a
 * component body (a new client every render, so everything refetches), and `cacheTime: 0` (a cold fetch
 * behind a spinner on every visit).
 *
 * The defaults suit an internal tool whose data other people are writing while you look at it: short
 * cache, one retry. Anything wanting different behaviour passes it in rather than editing this.
 */
export const createQueryClient = (config?: QueryClientConfig): QueryClient =>
    new QueryClient({
        ...config,
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                retry: 1,
                retryDelay: 500,
                staleTime: 30 * 1000,
                gcTime: 5 * 60 * 1000,
                ...config?.defaultOptions?.queries,
            },
            mutations: config?.defaultOptions?.mutations,
        },
    });
